import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { format, isSameDay } from 'date-fns';
import clsx from 'clsx';
import {
  ChatMessageBlockType,
  ChatMessageGeneralEventType,
  UniqueVisitor,
  User,
} from 'sb-lib/types';
import { findGeneralEventBlock } from 'sb-lib/chat-message-utils';
import { ActiveVisitor, ChatMessage } from 'types';
import { markChatMessageRead, updateChatMessage } from 'shared/store/chat-slice';
import { useIsVisitorTyping } from 'shared/hooks/chat-hooks';
import { renderMessage } from 'shared/utils/chat';
import { TypingIndicator } from 'browser/components/TypingIndicator';
import UserAvatar from 'shared/components/UserAvatar';
import { VisitorAvatar } from 'shared/components/VisitorAvatar';
import { SessionChatImage } from '../SessionChatImage';
import { Tooltip } from 'shared/components/Tooltip';
import './style.less';
import { Button } from 'shared/components/Button';
import { AIPersona } from 'types/ai-persona';
import DefaultRepAvatar from 'shared/assets/images/bella-idle-default.jpeg';

interface SessionChatMessageProps {
  message: ChatMessage;
  nextMessage?: ChatMessage;
  visitor: ActiveVisitor | UniqueVisitor | null;
  members: User[];
  isChatCollapsed: boolean;
  tooltipTheme?: 'light' | 'dark';
  aiPersonaAgent?: AIPersona;
}

export const SessionChatMessage: React.FC<SessionChatMessageProps> = ({
  message,
  nextMessage,
  visitor,
  members,
  isChatCollapsed,
  tooltipTheme,
  aiPersonaAgent,
}) => {
  const dispatch = useDispatch();
  const isVisitorTyping = useIsVisitorTyping(message);
  const isFromVisitor = message.sourceType === 'visitor';
  const isFromAgent = message.sourceType === 'agent';
  const isFromBot = message.sourceType === 'bot';
  const isFromAi = message.sourceType === 'ai';

  const isLastInSeries =
    nextMessage?.from !== message.from ||
    !isSameDay(nextMessage.timestamp, message.timestamp);

  const text = useMemo(() => {
    if (isVisitorTyping) {
      return <TypingIndicator />;
    }
    if (
      !message.text &&
      message.content.blocks.find(b => b.type === ChatMessageBlockType.SCHEDULE)
    ) {
      return <>[meeting booker]</>;
    }
    if (
      !message.text &&
      message.content.blocks.find(b => b.type === ChatMessageBlockType.EMAIL_INPUT)
    ) {
      return <>[email request]</>;
    }
    const generalEventBlock = findGeneralEventBlock(message);
    if (!message.text && generalEventBlock) {
      if (generalEventBlock.generalEventType === ChatMessageGeneralEventType.AGENT_CALL) {
        return <>[agent calling]</>;
      } else if (
        generalEventBlock.generalEventType ===
        ChatMessageGeneralEventType.VISITOR_MISSED_AGENT_CALL
      ) {
        return <>[visitor missed call]</>;
      } else if (
        generalEventBlock.generalEventType ===
        ChatMessageGeneralEventType.VISITOR_CALLBACK_AGENT
      ) {
        return <>[visitor callback CTA]</>;
      } else if (
        generalEventBlock.generalEventType === ChatMessageGeneralEventType.AGENT_TRANSFER
      ) {
        return <>[agent transfer]</>;
      }
    }
    return renderMessage(message.text, members);
  }, [message, members, isVisitorTyping]);

  useEffect(() => {
    if (!isFromAgent && !message.dateRead && !isChatCollapsed) {
      dispatch(markChatMessageRead({ messageId: message.id }));
    }
  }, [dispatch, isFromAgent, message.dateRead, message.id, isChatCollapsed]);

  const image = message.images && message.images.length ? message.images[0] : null;
  const imageId =
    message.imageIds && message.imageIds.length ? message.imageIds[0] : null;
  const propImage = image || (imageId && { id: imageId });

  return (
    <div
      className={clsx(
        'SessionChatMessage',
        !isFromVisitor && 'SessionChatMessage--fromUs',
      )}
    >
      {isFromVisitor && (
        <div className="SessionChatMessage__avatar">
          {isLastInSeries && <VisitorAvatar visitor={visitor} size={24} />}
        </div>
      )}
      <Tooltip
        title={
          <div className="SessionChatMessage__tooltip">
            <div>{format(new Date(message.timestamp), "iii, MMM do, Y 'at' p")}</div>
            {message.moderationStatus === 'rejected' && (
              <div>
                This message has been filtered by AI moderation due to potential
                inappropriate content.
              </div>
            )}
            {message.moderationStatus === 'rejected' &&
              !message.overrideModerationStatus && (
                <div>
                  <Button
                    size="small"
                    onClick={() =>
                      dispatch(
                        updateChatMessage({
                          id: message.id,
                          payload: { overrideModerationStatus: true },
                        }),
                      )
                    }
                  >
                    View message
                  </Button>
                </div>
              )}
            {message.moderationStatus === 'rejected' && message.overrideModerationStatus && (
              <div>
                <Button
                  size="small"
                  onClick={() =>
                    dispatch(
                      updateChatMessage({
                        id: message.id,
                        payload: { overrideModerationStatus: false },
                      }),
                    )
                  }
                >
                  Hide message
                </Button>
              </div>
            )}
          </div>
        }
        placement="left"
        theme={tooltipTheme ?? 'light'}
      >
        <div
          className={clsx(
            'SessionChatMessage-text',
            !isFromVisitor && 'SessionChatMessage-text--fromUs',
            isLastInSeries && 'SessionChatMessage-text--lastInSeries',
            !!propImage && 'SessionChatMessage-text--image',
          )}
        >
          {propImage ? <SessionChatImage image={propImage} /> : text}
        </div>
      </Tooltip>
      {!isFromVisitor &&
        (isLastInSeries ? (
          <Tooltip
            title={
              isFromAi ? aiPersonaAgent?.name ?? message.agent?.name : message.agent?.name
            }
            theme={'light'}
          >
            <div className="SessionChatMessage__avatarWrap">
              <UserAvatar
                className="SessionChatMessage__avatar"
                user={
                  isFromBot
                    ? { bot: true }
                    : isFromAi && aiPersonaAgent
                    ? {
                        ...aiPersonaAgent,
                        avatarUrl: aiPersonaAgent?.default
                          ? DefaultRepAvatar
                          : aiPersonaAgent?.avatarUrl,
                        ai: true,
                      }
                    : message.agent
                }
                size={32}
              />
              {isFromAi && <div className="SessionChatMessage__aiBadge">AI</div>}
            </div>
          </Tooltip>
        ) : (
          <div className="SessionChatMessage__avatar" />
        ))}
    </div>
  );
};
